import type { GoogleAdsConfig } from '@sgcmpteltd/uikit-next/types';

import {
  isCategoryType,
  isNewCarFuelType,
  isNewCarSortType,
} from '@/apis/server/new-car/v1/utils';
import {
  AUTHORISED_DISTRIBUTORS,
  PARALLEL_IMPORTERS,
  VEHICLE_VALUE_ALL_COMMERCIAL_VEHICLES,
  VEHICLE_VALUE_ALL_PASSENGER_CARS,
  VEHICLE_VALUE_ELECTRIC_CARS,
  VEHICLE_VALUE_HATCHBACK,
  VEHICLE_VALUE_HYBRID_CARS,
  VEHICLE_VALUE_LUXURY_SEDAN,
  VEHICLE_VALUE_MID_SIZED_SEDAN,
  VEHICLE_VALUE_MPV,
  VEHICLE_VALUE_SPORTS_CARS,
  VEHICLE_VALUE_STATIONWAGON,
  VEHICLE_VALUE_SUV,
} from '@/constants/global';
import { PHP_MAX_INTEGER_LIMIT } from '@/constants/system';

import type {
  CollectSearchDataConfig,
  OnSearchData,
  SearchFilterTypeMap,
} from './types';

export const convertToCamelCase = (input: string) => input
  .toLowerCase()
  .replace(/[^a-zA-Z0-9]+(.)/g, (match, char) => char.toUpperCase());

export const isInteger = (
  input: unknown,
  range: [lower: number, upper: number],
) => (
  Number.isInteger(input)
  && typeof input === 'number'
  && input >= range[0]
  && input <= range[1]
);

export const categoryTypeToText = (category: 'ad' | 'pi' | 'a' | 'b') => {
  switch (category) {
    case 'ad':
      return 'authorised-distributor';
    case 'pi':
      return 'parallel-importer';
    case 'a':
      return 'cat-a-coe';
    case 'b':
      return 'cat-b-coe';
    default:
      return 'any';
  }
};

// validate and parse query string from url and convert to data
export const collectSearchData: (
  config: CollectSearchDataConfig
) => OnSearchData = ({
  searchParams,
}) => {
  const make = typeof searchParams.make === 'string'
    ? searchParams.make.trim()
    : undefined;
  const category = isCategoryType(searchParams.category)
    ? searchParams.category
    : undefined;
  const coeCategory = typeof searchParams.coe_category === 'string'
    ? searchParams.coe_category.trim()
    : undefined;
  const country = typeof searchParams.country === 'string'
    ? searchParams.country.trim()
    : undefined;
  const dealerId = typeof searchParams.dealer_id === 'string'
    && Number.isInteger(+(searchParams.dealer_id))
    ? +(searchParams.dealer_id)
    : undefined;
  const dealerType = searchParams.dealer_type === AUTHORISED_DISTRIBUTORS
    || searchParams.dealer_type === PARALLEL_IMPORTERS
    ? searchParams.dealer_type
    : undefined;
  const fuelType = isNewCarFuelType(searchParams.fuel_type)
    ? searchParams.fuel_type
    : undefined;
  const order = searchParams.order === 'asc'
    || searchParams.order === 'desc'
    ? searchParams.order
    : undefined;
  const q = typeof searchParams.q === 'string'
    && searchParams.q.length <= 50
    ? searchParams.q.trim()
    : undefined;
  const year = typeof searchParams.year === 'string'
    && isInteger(+(searchParams.year), [2002, 9999])
    ? searchParams.year
    : undefined;
  const sortBy = isNewCarSortType(searchParams.sort_by)
    ? searchParams.sort_by
    : undefined;
  const transmission = typeof searchParams.transmission === 'string'
    ? searchParams.transmission.trim()
    : undefined;
  const vehicleType = (typeof searchParams.vehicle_type === 'string'
    && searchParams.vehicle_type !== 'any')
    ? searchParams.vehicle_type.trim()
    : undefined;
  const page = typeof searchParams.page === 'string'
    && isInteger(+(searchParams.page), [1, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.page)
    : undefined;
  const limit = typeof searchParams.limit === 'string'
    && isInteger(+(searchParams.limit), [15, 99])
    ? +(searchParams.limit)
    : undefined;
  const maxDepreciation = typeof searchParams.max_depreciation === 'string'
    && isInteger(+(searchParams.max_depreciation), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.max_depreciation)
    : undefined;
  const maxDriveRange = typeof searchParams.max_drive_range === 'string'
    && isInteger(+(searchParams.max_drive_range), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.max_drive_range)
    : undefined;
  const maxEngineCapacity = typeof searchParams.max_engine_capacity === 'string'
    && isInteger(+(searchParams.max_engine_capacity), [0, 9999])
    ? +(searchParams.max_engine_capacity)
    : undefined;
  const maxFuelEconomy = typeof searchParams.max_fuel_economy === 'string'
    && isInteger(+(searchParams.max_fuel_economy), [0, 9999])
    ? +(searchParams.max_fuel_economy)
    : undefined;
  const maxOmv = typeof searchParams.max_omv === 'string'
    && isInteger(+(searchParams.max_omv), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.max_omv)
    : undefined;
  const maxPowerOutput = typeof searchParams.max_power_output === 'string'
    && isInteger(+(searchParams.max_power_output), [0, 9999])
    ? +(searchParams.max_power_output)
    : undefined;
  const maxEnergyConsumption = typeof searchParams.max_energy_consumption
    === 'string'
    && isInteger(+(searchParams.max_energy_consumption), [0, 9999])
    ? +(searchParams.max_energy_consumption)
    : undefined;
  const maxPrice = typeof searchParams.max_price === 'string'
    && isInteger(+(searchParams.max_price), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.max_price)
    : undefined;
  const minDepreciation = typeof searchParams.min_depreciation === 'string'
    && isInteger(+(searchParams.min_depreciation), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.min_depreciation)
    : undefined;
  const minDriveRange = typeof searchParams.min_drive_range === 'string'
    && isInteger(+(searchParams.min_drive_range), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.min_drive_range)
    : undefined;
  const minEngineCapacity = typeof searchParams.min_engine_capacity === 'string'
    && isInteger(+(searchParams.min_engine_capacity), [0, 9999])
    ? +(searchParams.min_engine_capacity)
    : undefined;
  const minFuelEconomy = typeof searchParams.min_fuel_economy === 'string'
    && isInteger(+(searchParams.min_fuel_economy), [0, 9999])
    ? +(searchParams.min_fuel_economy)
    : undefined;
  const minOmv = typeof searchParams.min_omv === 'string'
    && isInteger(+(searchParams.min_omv), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.min_omv)
    : undefined;
  const minPowerOutput = typeof searchParams.min_power_output === 'string'
    && isInteger(+(searchParams.min_power_output), [0, 9999])
    ? +(searchParams.min_power_output)
    : undefined;
  const minEnergyConsumption = typeof searchParams.min_energy_consumption
    === 'string'
    && isInteger(+(searchParams.min_energy_consumption), [0, 9999])
    ? +(searchParams.min_energy_consumption)
    : undefined;
  const minPrice = typeof searchParams.min_price === 'string'
    && isInteger(+(searchParams.min_price), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.min_price)
    : undefined;

  return {
    query: {
      make,
      category,
      coeCategory,
      country,
      dealerId,
      dealerType,
      fuelType,
      order,
      q,
      year,
      sortBy,
      transmission,
      vehicleType,
      page,
      limit,
      maxDepreciation,
      maxDriveRange,
      maxEngineCapacity,
      maxEnergyConsumption,
      maxFuelEconomy,
      maxOmv,
      maxPowerOutput,
      maxPrice,
      minDepreciation,
      minDriveRange,
      minEngineCapacity,
      minEnergyConsumption,
      minFuelEconomy,
      minOmv,
      minPowerOutput,
      minPrice,
    },
  };
};

export const collectTargetMapping: (
  query: SearchFilterTypeMap,
  brandList: string[],
) => Record<string, string | string[]> = (
  query,
  brandList,
) => {
  const lowerCaseBrandList = brandList.map(brand => brand.toLowerCase());
  const target: Record<string, string[]> = {
    newcarMake: [],
    newcarType: [],
    newcarPrice: [],
    newcarCategory: [],
  };

  if (query.make) {
    target.newcarMake.push(query.make.toLowerCase().split(' ').join('-'));
  }

  if (query.q) {
    if (lowerCaseBrandList.includes(query.q.toLowerCase())) {
      if (query.q.toLowerCase() !== query.make?.toLowerCase()) {
        target.newcarMake.push(query.q.toLowerCase().split(' ').join('-'));
      }
    }

    if (query.q.toLowerCase().includes('convertible')) {
      target.newcarType.push('convertible');
    }

    if (query.q.toLowerCase().includes('coupe')) {
      target.newcarType.push('coupe');
    }

    if (query.q.toLowerCase().includes('crossover')) {
      target.newcarType.push('crossover');
    }

    if (query.q.toLowerCase().includes('van')) {
      target.newcarType.push('van');
    }

    if (query.q.toLowerCase().includes('bus')) {
      target.newcarType.push('bus');
    }

    if (query.q.toLowerCase().includes('truck')) {
      target.newcarType.push('truck');
    }

    if (query.q.toLowerCase().includes('diesel')) {
      target.newcarType.push('diesel');
    }
  }

  if (query.maxPrice) {
    target.newcarPrice.push(`max${(query.maxPrice / 1000).toString()}k`);

    if (query.maxPrice <= 150000) {
      target.newcarType.push('budget-car');
    }
  }

  if (query.minPrice) {
    if (query.minPrice >= 300000) {
      target.newcarType.push('luxury-car');
    }
  }

  if (query.minPrice && query.maxPrice) {
    if (query.minPrice >= 160000 && query.maxPrice <= 300000) {
      target.newcarType.push('mid-range-car');
    }
  }

  if (query.vehicleType) {
    if (query.vehicleType === VEHICLE_VALUE_ALL_PASSENGER_CARS) {
      target.newcarType.push('passengers-car');
    } else if (query.vehicleType === VEHICLE_VALUE_ALL_COMMERCIAL_VEHICLES) {
      target.newcarType.push('commercial-vehicles');
    } else if (query.vehicleType === VEHICLE_VALUE_SPORTS_CARS) {
      target.newcarType.push('sports-car');
    } else if (query.vehicleType === VEHICLE_VALUE_LUXURY_SEDAN) {
      target.newcarType.push('luxury-sedan');
    } else {
      target.newcarType.push(
        query.vehicleType.toLowerCase().split(' ').join('-'),
      );
    }
  }

  if (query.category) {
    target.newcarCategory.push(categoryTypeToText(query.category));
  }

  Object.keys(target).forEach((key) => {
    if (target[key].length === 0) {
      delete target[key];
    }
  });

  return target;
};

export const getGlobalTargetingMap = ({
  pathParams,
  searchParams,
}: {
  pathParams: SearchFilterTypeMap;
  searchParams: URLSearchParams;
}) => {
  const targetingMap: GoogleAdsConfig['targetingMap'] = {
    pageType: 'new-cars',
  };

  // Car Make targeting
  const carMakePatterns: Record<string, string[]> = {
    aion: ['aion'],
    'alfa-romeo': ['alfa%20romeo', 'alfa+romeo', 'alfaromeo', 'alfa-romeo'],
    alpine: ['alpine'],
    'aston-martin': [
      'aston%20martin',
      'aston+martin',
      'astonmartin',
      'aston-martin',
    ],
    audi: ['audi'],
    bac: ['bac'],
    bentley: ['bentley'],
    bmw: ['bmw'],
    'bmw-alpina': ['bmw%20alpina', 'bmw+alpina', 'bmwalpina', 'bmw-alpina'],
    byd: ['byd'],
    chevrolet: ['chevrolet'],
    citroen: ['citroen'],
    cupra: ['cupra'],
    deepal: ['deepal'],
    dfsk: ['dfsk'],
    dongfeng: ['dongfeng'],
    farizon: ['farizon'],
    ferrari: ['ferrari'],
    ford: ['ford'],
    foton: ['foton'],
    gac: ['gac'],
    'golden-dragon': [
      'golden%20dragon',
      'golden+dragon',
      'goldendragon',
      'golden-dragon',
    ],
    higer: ['higer'],
    hino: ['hino'],
    honda: ['honda'],
    hyundai: ['hyundai'],
    isuzu: ['isuzu'],
    jaguar: ['jaguar'],
    jaecoo: ['jaecoo'],
    jeep: ['jeep'],
    joylong: ['joylong'],
    kia: ['kia'],
    kyc: ['kyc'],
    lamborghini: ['lamborghini'],
    'land-rover': ['land%20rover', 'land+rover', 'landrover', 'land-rover'],
    levc: ['levc'],
    lexus: ['lexus'],
    lotus: ['lotus'],
    maserati: ['maserati'],
    maxus: ['maxus'],
    mazda: ['mazda'],
    mclaren: ['mclaren'],
    'mercedes-benz': [
      'mercedes%20benz',
      'mercedes+benz',
      'mercedesbenz',
      'mercedes',
      'mercedes-benz',
    ],
    mg: ['mg'],
    mini: ['mini'],
    mitsubishi: ['mitsubishi'],
    mitsuoka: ['mitsuoka'],
    morgan: ['morgan'],
    nissan: ['nissan'],
    ohm: ['ohm'],
    omoda: ['omoda'],
    opel: ['opel'],
    ora: ['ora'],
    pagani: ['pagani'],
    perodua: ['perodua'],
    peugeot: ['peugeot'],
    polestar: ['polestar'],
    porsche: ['porsche'],
    renault: ['renault'],
    'rolls-royce': [
      'rolls%20royce',
      'rolls+royce',
      'rollsroyce',
      'rolls-royce',
    ],
    sany: ['sany'],
    seres: ['seres'],
    skoda: ['skoda'],
    skyworth: ['skyworth'],
    smart: ['smart'],
    sokon: ['sokon'],
    srm: ['srm'],
    ssangyong: ['ssangyong'],
    subaru: ['subaru'],
    suzuki: ['suzuki'],
    tesla: ['tesla'],
    toyota: ['toyota'],
    victory: ['victory'],
    volkswagen: ['volkswagen'],
    volvo: ['volvo'],
    xpeng: ['xpeng'],
    yutong: ['yutong'],
    yaxing: ['yaxing'],
    zeekr: ['zeekr'],
  };

  // Car Type targeting
  const carTypePatterns: Record<string, string[]> = {
    'hybrid-cars': [VEHICLE_VALUE_HYBRID_CARS],
    'electric-cars': [VEHICLE_VALUE_ELECTRIC_CARS],
    hatchback: [VEHICLE_VALUE_HATCHBACK],
    'luxuary-sedan': [VEHICLE_VALUE_LUXURY_SEDAN],
    mpv: [VEHICLE_VALUE_MPV],
    'mid-sized-sedan': [VEHICLE_VALUE_MID_SIZED_SEDAN],
    sports: [VEHICLE_VALUE_SPORTS_CARS],
    stationwagon: [VEHICLE_VALUE_STATIONWAGON],
    suv: [VEHICLE_VALUE_SUV],
    commercial: [VEHICLE_VALUE_ALL_COMMERCIAL_VEHICLES],
    passenger: [VEHICLE_VALUE_ALL_PASSENGER_CARS],
  };

  // Car Price targeting - consolidated approach
  const priceRanges = [
    { key: '70k', value: 70000 },
    { key: '80k', value: 80000 },
    { key: '90k', value: 90000 },
    { key: '100k', value: 100000 },
    { key: '110k', value: 110000 },
    { key: '120k', value: 120000 },
    { key: '130k', value: 130000 },
    { key: '140k', value: 140000 },
    { key: '150k', value: 150000 },
    { key: '160k', value: 160000 },
    { key: '170k', value: 170000 },
    { key: '180k', value: 180000 },
    { key: '190k', value: 190000 },
    { key: '200k', value: 200000 },
    { key: '210k', value: 210000 },
    { key: '220k', value: 220000 },
    { key: '230k', value: 230000 },
    { key: '240k', value: 240000 },
    { key: '250k', value: 250000 },
    { key: '300k', value: 300000 },
    { key: '350k', value: 350000 },
    { key: '400k', value: 400000 },
    { key: '500k', value: 500000 },
    { key: '600k', value: 600000 },
    { key: '700k', value: 700000 },
  ];

  const generatePricePatterns = (prefix: 'min' | 'max') => {
    const patterns: Record<string, string[]> = {};

    priceRanges.forEach(({ key, value }) => {
      const patternKey = `${prefix}${key}`;

      patterns[patternKey] = [`${prefix}_price=${value}`];

      // Add camelCase variant for certain ranges
      if ([
        '80k', '100k', '120k', '140k', '160k', '180k', '200k', '240k', '300k',
      ].includes(key)) {
        patterns[patternKey].push(`${prefix}Price=${value}`);
      }
    });
    return patterns;
  };

  const carPricePatterns = {
    ...generatePricePatterns('max'),
    ...generatePricePatterns('min'),
  };

  const carPriceMinPatterns = generatePricePatterns('min');
  const carPriceMaxPatterns = generatePricePatterns('max');

  // Car Fuel Type targeting
  const carFuelTypePatterns: Record<string, string[]> = {
    petrol: ['petrol'],
    diesel: ['diesel'],
    'petrol-electric': ['petrol-electric', 'petrol%20electric'],
    'diesel-electric': ['diesel-electric', 'diesel%20electric'],
    electric: ['electric'],
  };

  // Helper function to check URL patterns
  const checkUrlPatterns = (
    patterns: Record<string, string[]>,
  ): string | string[] => {
    const matches: string[] = [];

    for (const [key, patternList] of Object.entries(patterns)) {
      if (patternList.some(
        pattern => searchParams.toString().toLowerCase().includes(
          pattern.toLowerCase(),
        ),
      )) {
        matches.push(key);
      }
    }
    return matches.length === 1 ? matches[0] : matches;
  };

  // Check for car make
  const carMakes = checkUrlPatterns(carMakePatterns);

  if (carMakes.length > 0) {
    targetingMap.carMake = carMakes;
  } else if (pathParams.make) {
    targetingMap.carMake = [
      pathParams.make.toLowerCase().split(' ').join('-'),
    ];
  }

  // Check for car type
  const carTypes = checkUrlPatterns(carTypePatterns);

  if (carTypes.length > 0) {
    targetingMap.carType = carTypes;
  } else if (pathParams.vehicleType) {
    const vehicleTypeMapping: Record<string, string> = {
      [VEHICLE_VALUE_ALL_PASSENGER_CARS]: 'passengers-car',
      [VEHICLE_VALUE_ALL_COMMERCIAL_VEHICLES]: 'commercial-vehicles',
      [VEHICLE_VALUE_SPORTS_CARS]: 'sports-car',
      [VEHICLE_VALUE_LUXURY_SEDAN]: 'luxury-sedan',
    };

    const mappedType = vehicleTypeMapping[pathParams.vehicleType]
      ?? pathParams.vehicleType.toLowerCase().split(' ').join('-');

    targetingMap.carType = [mappedType];
  }

  // Check for car price
  const carPrices = checkUrlPatterns(carPricePatterns);

  if (carPrices.length > 0) {
    targetingMap.carPrice = carPrices;
  }

  // Check for car price
  const carPriceMin = checkUrlPatterns(carPriceMinPatterns);

  if (carPriceMin.length > 0) {
    targetingMap.carPriceMin = carPriceMin;
  }

  // Check for car price
  const carPriceMax = checkUrlPatterns(carPriceMaxPatterns);

  if (carPriceMax.length > 0) {
    targetingMap.carPriceMax = carPriceMax;
  }

  // Check for car fuel type
  const carFuelTypes = checkUrlPatterns(carFuelTypePatterns);

  if (carFuelTypes.length > 0) {
    targetingMap.carFuelType = carFuelTypes;
  }

  return targetingMap;
};
